<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import { usePageNavigationCustomisation } from '../composables/usePageNavigationCustomisation';
import {
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  combineStyles,
} from '@/common/customisation/utils/styleUtils';
import { ACTION_ICON_MAP, ACTION_LINK_MAP, NavigationAction } from '../utils/navigationConfig';

interface Props {
  sectionKey: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'open-widget'): void;
}>();

const router = useRouter();
const customisation = usePageNavigationCustomisation(props.sectionKey);

const actionIcon = computed(() => {
  const actionType = customisation.actionType.value;
  if (!actionType) return null;

  return ACTION_ICON_MAP[actionType] || null;
});

const containerStyle = computed(() =>
  combineStyles(createBackgroundStyle(customisation.backgroundColor), createBorderStyle(customisation.borderColor))
);

const labelStyle = computed(() => createTextStyle(customisation.labelColor));

const actionIconStyle = computed(() => createTextStyle(customisation.actionColor));

const handleClick = () => {
  const actionType = customisation.actionType.value;
  if (!actionType || !router) return;

  const route = ACTION_LINK_MAP[actionType];
  if (route.path) {
    router.push(route);
  } else if (route.action) {
    switch (route.action) {
      case NavigationAction.OPEN_WIDGET:
        emit('open-widget');
        break;
    }
  }
};

const shouldRender = computed(() => {
  if (!customisation.isVisible.value) {
    return false;
  }
  return customisation.labelText.value?.trim() !== '';
});

const ariaLabel = computed(() => {
  return customisation.labelText.value || 'Navigation link';
});
</script>

<template>
  <div
    v-if="shouldRender"
    class="card-radius page-navigation-row shadow-soft"
    :style="containerStyle"
    tabindex="0"
    :aria-label="ariaLabel"
    :data-testid="`nav-row-${customisation.actionType.value}`"
    @click="handleClick"
  >
    <div class="content">
      <component :is="actionIcon" v-if="actionIcon" class="icon" :style="labelStyle" />
      <span class="label theme-font-main" :style="labelStyle">
        {{ customisation.labelText.value }}
      </span>
    </div>
    <span class="action-icon" :style="actionIconStyle">›</span>
  </div>
</template>

<style scoped lang="scss">
.page-navigation-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  border: 1px solid;
}

.content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.icon {
  font-size: 20px;
}

.label {
  font-size: 16px;
  font-weight: 500;
}

.action-icon {
  font-size: 24px;
  font-weight: 300;
}
</style>
